import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle, XCircle, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { verifyEmail, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying, success, error
  const [message, setMessage] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Redirect if already authenticated
    if (isAuthenticated) {
      navigate('/explore', { replace: true });
      return;
    }

    // Prevent double processing
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const token = searchParams.get('token');
    
    if (!token) {
      setStatus('error');
      setMessage('Geçersiz doğrulama linki');
      return;
    }

    const verify = async () => {
      const result = await verifyEmail(token);
      
      if (result.success) {
        setStatus('success');
        setMessage(result.message);
        // Redirect to explore after 3 seconds
        setTimeout(() => {
          navigate('/explore', { replace: true });
        }, 3000);
      } else {
        setStatus('error');
        setMessage(result.error);
      }
    };

    verify();
  }, [searchParams, verifyEmail, navigate, isAuthenticated]);

  return (
    <div className="min-h-screen bg-[#e8f4f8] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2d1b4e] to-[#1a1040] flex items-center justify-center shadow-lg overflow-hidden">
            <img 
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692cc1ea9c8a6553034e2bc3/40c4882f7_logo.png" 
              alt="Giresun Kent Rehberi" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 animate-spin text-[#1e3a5f] mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">E-posta Doğrulanıyor...</h2>
            <p className="text-gray-500">Lütfen bekleyin</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">E-posta Doğrulandı!</h2>
            <p className="text-gray-500 mb-6">{message}</p>
            <p className="text-sm text-gray-400">Birkaç saniye içinde yönlendirileceksiniz...</p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Doğrulama Başarısız</h2>
            <p className="text-red-500 mb-6">{message}</p>
            <Link 
              to="/login"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#1e3a5f] text-white rounded-xl hover:bg-[#2a4a73] transition-colors"
            >
              <Mail className="w-4 h-4" />
              Giriş Sayfasına Dön
            </Link>
          </>
        )}
      </div>
    </div>
  );
};

export default VerifyEmailPage;
